import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FolderSearchIcon, GraduationCapIcon, LayersIcon, PenLineIcon, SpellCheckIcon, UserMinusIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { pageSamples as samples } from './samples';

// POLSKI GOTOWY™ copy (Polish). Single source: polski-gotowy/PRODUCTO.md.
// Product mockup (WebP 2048², transparent) — see polski-gotowy/media/MOCKUP.md.
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/01060a17-12b5-462e-9c45-c87779a7c0e4/1792787842019_polski-gotowy-mockup-6-tomow-58cf84f8.webp';
export const DISCLAIMER = 'POLSKI GOTOWY™ to niezależny materiał dla nauczycieli, oparty na podstawie programowej języka polskiego. Nie jest publikacją MEN ani CKE. Zadania w stylu egzaminu ósmoklasisty są autorskie – to nie są arkusze CKE.';
export const heroGuarantees = ['Natychmiastowy dostęp do pobrania', 'Jednorazowa płatność', 'Bez subskrypcji', '7 dni gwarancji satysfakcji'];
export const stats = [{
  value: '6',
  label: 'Cyfrowych tomów dla polonisty'
}, {
  value: '240',
  label: 'Stron gotowych materiałów'
}, {
  value: '12',
  label: 'Lekcji minuta po minucie w Tomie 1'
}, {
  value: '16',
  label: 'Kart pracy do lektur'
}, {
  value: '5',
  label: 'Bonusów w zestawie'
}];
export const differenceStats = [{
  value: 'Klasy 4–8',
  label: 'Od pierwszej lektury w 4 klasie do egzaminu ósmoklasisty'
}, {
  value: '3 poziomy',
  label: 'Karty pracy i ćwiczenia dla całej klasy – z kluczem odpowiedzi'
}, {
  value: 'PDF do druku',
  label: 'Na tablicy interaktywnej albo na kartce w ławce'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Niedzielny wieczór na planowaniu',
  text: 'Zamiast odpocząć, siedzisz nad konspektem na poniedziałkową lekcję – i znowu zaczynasz od pustej kartki.'
}, {
  title: 'Jedna klasa, trzy poziomy',
  text: 'Jedni czytają płynnie i piszą rozprawki, inni mają kłopot z ortografią. Jednym jest za trudno, drudzy się nudzą.'
}, {
  title: 'Lektura za lekturą',
  text: 'Do każdej lektury szukasz kart pracy, pytań i tematów wypracowań – a w internecie wszystko jest rozproszone.'
}, {
  title: 'Gramatyka bez klucza',
  text: 'Ćwiczenia z gramatyki i ortografii przygotowujesz sam i sam sprawdzasz – bez gotowych odpowiedzi.'
}, {
  title: 'Presja egzaminu ósmoklasisty',
  text: 'Ósma klasa, czytanie ze zrozumieniem, wypracowanie – a czasu na powtórki coraz mniej.'
}, {
  title: 'Sprawdziany, oceny i komentarze',
  text: 'Sprawdziany w dwóch grupach, kryteria i komentarze do ocen – wszystko robisz po lekcjach.'
}];
export const afterItems = ['Plan wynikowy i rozkład roku dla klas 4–8', '12 lekcji minuta po minucie (45 min) w Tomie 1', 'Karty ucznia w 3 poziomach – cała klasa pracuje naraz', '16 kart pracy do lektur z kluczem', 'Gramatyka i ortografia w 3 poziomach z kluczem odpowiedzi', '10 form wypowiedzi: wzór, szablon i kryteria', '6 zestawów ćwiczeń w stylu egzaminu ósmoklasisty', 'Sprawdziany A/B i 100 gotowych komentarzy do ocen', 'Wszystko w PDF do druku'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Niedzielny wieczór = planowanie',
  text: 'Czy w niedzielę wieczorem zamiast odpoczywać przygotowujesz lekcje polskiego na cały tydzień?'
}, {
  icon: UsersIcon,
  title: 'Różne poziomy w jednej klasie',
  text: 'Czy w jednej klasie masz uczniów, którzy czytają z pasją, i takich, którzy nie przeczytali lektury?'
}, {
  icon: UserMinusIcon,
  title: 'Uczniowie się nudzą',
  text: 'Czy przy omawianiu lektury część klasy przestaje słuchać po kilku minutach?'
}, {
  icon: GraduationCapIcon,
  title: 'Egzamin ósmoklasisty',
  text: 'Czy chcesz przygotować ósmoklasistów krok po kroku – bez stresu w ostatnich tygodniach?'
}, {
  icon: FolderSearchIcon,
  title: 'Kończą Ci się pomysły',
  text: 'Czy szukasz nowych kart pracy, ćwiczeń i tematów wypracowań, które zaangażują całą klasę?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Sprawdziany i ocenianie',
  text: 'Czy przed każdym sprawdzianem znowu przygotowujesz grupy A i B, klucz i kryteria od zera?'
}];

// Spine colours mirror the box (navy, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Tom 01',
  level: 'Planowanie',
  title: 'Plan wynikowy + 12 lekcji minuta po minucie',
  tagline: 'Wiesz, co robić na lekcji – od września do czerwca.',
  intro: 'Plan wynikowy dla klas 4–6 i 7–8 (wymagania podstawowe i ponadpodstawowe), rozkład roku miesiąc po miesiącu i 12 lekcji po 45 minut – każda ze stroną nauczyciela i kartą ucznia w 3 poziomach.',
  items: ['Plan wynikowy dla klas 4–6 i 7–8', 'Rozkład roku miesiąc po miesiącu', 'Plan 12 tygodni i tabela progresji', '12 lekcji minuta po minucie (45 min)', 'Karta ucznia w 3 poziomach', 'Sprawdzian, klucz i kryteria oceniania'],
  outro: 'Otwierasz, wybierasz lekcję i wchodzisz do klasy z planem w 5 minut.',
  spine: 'bg-blue-900 text-white',
  rule: 'border-white/30'
}, {
  number: 'Tom 02',
  level: 'Lektury',
  title: 'Lektury bez stresu: 16 kart pracy',
  tagline: 'Gotowa karta pracy do każdej omawianej lektury.',
  intro: 'Do każdej lektury: o autorze w 3 zdaniach, czas i miejsce akcji, bohaterowie, plan wydarzeń do uzupełnienia, 6 pytań w 3 poziomach, zadanie twórcze i klucz odpowiedzi.',
  items: ['Mikołajek, Akademia pana Kleksa, Opowieści z Narnii', 'Mity greckie, legendy polskie, bajki Krasickiego', 'Chłopcy z Placu Broni, Opowieść wigilijna, Latarnik', 'Zemsta, Balladyna, Dziady cz. II, Treny', 'Pan Tadeusz (fragmenty), Mały Książę, Kamienie na szaniec', 'Ściągawka „motywy literackie” i tematy wypracowań'],
  outro: 'Lektura omówiona – bez szukania materiałów po nocach.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tom 03',
  level: 'Gramatyka',
  title: 'Gramatyka i ortografia: ćwiczenia w 3 poziomach z kluczem',
  tagline: 'Ćwiczenia gotowe do druku – i od razu z kluczem.',
  intro: '20 kart ćwiczeń z gramatyki i ortografii w trzech poziomach trudności, dyktanda i pełny klucz odpowiedzi.',
  items: ['Części mowy i części zdania', 'Zdanie pojedyncze i złożone', 'Interpunkcja: przecinek', 'Ortografia: ó–u, rz–ż, ch–h, „nie” z różnymi częściami mowy', 'Słowotwórstwo, frazeologia, głoski i sylaby', '20 kart ćwiczeń, dyktanda i klucz'],
  outro: 'Mniej sprawdzania, więcej czasu na naukę.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Tom 04',
  level: 'Wypowiedzi',
  title: 'Formy wypowiedzi: 10 form z wzorem, szablonem i kryteriami',
  tagline: 'Uczniowie wiedzą, jak napisać – a Ty, jak ocenić.',
  intro: 'Do każdej formy: wzorcowy tekst, szablon, lista kontrolna ucznia, kryteria oceny i typowe błędy.',
  items: ['Opowiadanie (twórcze, z dialogiem)', 'Opis i charakterystyka postaci', 'List prywatny i oficjalny', 'Sprawozdanie i recenzja', 'Rozprawka i przemówienie', 'Zaproszenie, ogłoszenie i notatka'],
  outro: 'Wypracowania z jasnymi kryteriami – dla Ciebie i dla uczniów.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tom 05',
  level: 'Egzamin',
  title: 'Egzamin ósmoklasisty: przygotowanie krok po kroku',
  tagline: 'Ósmoklasiści przygotowani spokojnie i systematycznie.',
  intro: 'Jak wygląda arkusz, strategie czytania ze zrozumieniem, 6 zestawów ćwiczeń w stylu egzaminu z kluczem, bank argumentów z lektur i plan powtórek na 8 tygodni.',
  items: ['Jak wygląda arkusz (część I i II)', 'Strategie czytania ze zrozumieniem', '6 zestawów ćwiczeń w stylu egzaminu z kluczem', 'Bank argumentów z lektur', 'Wypracowanie: plan, kryteria i 3 przykładowe prace', 'Plan powtórek na 8 tygodni i checklista'],
  outro: 'Powtórki rozpisane tydzień po tygodniu.',
  note: 'Własne teksty i zadania w stylu egzaminu ósmoklasisty – to nie są arkusze CKE.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tom 06',
  level: 'Ocenianie',
  title: 'Ocenianie: kryteria, sprawdziany A/B i 100 komentarzy',
  tagline: 'Sprawiedliwe ocenianie bez tworzenia dokumentów od zera.',
  intro: 'Przedmiotowe zasady oceniania, kryteria na oceny 1–6, sprawdziany i kartkówki w grupach A/B z kluczem, ocenianie kształtujące i 100 gotowych komentarzy.',
  items: ['Przedmiotowe zasady oceniania (PZO)', 'Kryteria na oceny 1–6', 'Sprawdziany A/B z kluczem: gramatyka, lektura, ortografia', 'Kartkówki i kryteria wypracowania', 'Ocenianie kształtujące: NaCoBeZu i informacja zwrotna', '100 gotowych komentarzy i arkusz obserwacji ucznia'],
  outro: 'Ocena i informacja zwrotna – szybko i konsekwentnie.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 cyfrowych tomów dla polonisty',
  text: 'System sześć w jednym do nauczania języka polskiego w klasach 4–8 – oparty na podstawie programowej, przez cały rok szkolny, od września do czerwca.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Gotowe lekcje',
  text: '12 lekcji minuta po minucie ze stroną nauczyciela i kartą ucznia – nie zaczynasz od pustej kartki.'
}, {
  icon: LayersIcon,
  title: '3 poziomy trudności',
  text: 'Karty pracy i ćwiczenia w trzech poziomach – każdy uczeń pracuje na swoim poziomie.'
}, {
  icon: SpellCheckIcon,
  title: 'Zawsze z kluczem',
  text: 'Gramatyka, ortografia, lektury i sprawdziany z kluczem odpowiedzi – mniej sprawdzania.'
}, {
  icon: PenLineIcon,
  title: 'Wypracowania krok po kroku',
  text: '10 form wypowiedzi z wzorem, szablonem, listą kontrolną i kryteriami oceny.'
}, {
  icon: GraduationCapIcon,
  title: 'Egzamin ósmoklasisty',
  text: '6 zestawów ćwiczeń w stylu egzaminu z kluczem i plan powtórek na 8 tygodni.'
}, {
  icon: CalendarCheckIcon,
  title: 'Plan na cały rok',
  text: 'Plan wynikowy i rozkład materiału od września do czerwca dla klas 4–8.'
}];

// Real Tom 1 sample pages live in data/samples.ts (empty = section hidden).
export const pageSamples = samples;
export const pageTypes = ['Plan wynikowy', 'Lekcje minuta po minucie', 'Karty pracy', 'Klucz odpowiedzi'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Rozgrzewki językowe w 5 minut',
  text: '60 krótkich zabaw na początek lekcji: rebusy, krzyżówki słowne i łańcuchy wyrazów.',
  items: ['60 zabaw językowych', 'Rebusy i krzyżówki słowne', 'Łańcuchy wyrazów'],
  benefit: 'Sprawny i aktywny start każdej lekcji.'
}, {
  title: 'Dyktanda gotowe do lekcji',
  text: '30 dyktand w 3 poziomach – z zasadami ortograficznymi i kluczem.',
  items: ['30 dyktand', '3 poziomy trudności', 'Zasady i klucz'],
  benefit: 'Ortografia ćwiczona regularnie, bez przygotowań.'
}, {
  title: 'Zastępstwa z polskiego bez stresu',
  text: '20 lekcji na zastępstwo bez przygotowania dla klas 4–8.',
  items: ['20 gotowych lekcji', 'Klasy 4–8', 'Łatwe do przekazania'],
  benefit: 'Spokój, gdy musisz nagle przejąć klasę.'
}, {
  title: 'Komunikacja z rodzicami',
  text: 'Szablony wiadomości, scenariusz wywiadówki i pomysły na czytelnictwo w domu.',
  items: ['Szablony wiadomości', 'Wywiadówka krok po kroku', 'Czytelnictwo w domu'],
  benefit: 'Mniej czasu na pisanie do rodziców.'
}, {
  title: 'Ściągawki na ścianę: plakaty do sali polonistycznej',
  text: 'Plakaty do druku: części mowy, interpunkcja, ó/u i rz/ż, formy wypowiedzi.',
  items: ['Części mowy', 'Interpunkcja i ortografia', 'Formy wypowiedzi'],
  benefit: 'Sala, która codziennie przypomina o zasadach.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Otwórz POLSKI GOTOWY™',
  text: 'Pobierz pakiet i otwórz potrzebny tom.'
}, {
  title: 'Wybierz lekcję',
  text: 'Wybierz lekcję, kartę pracy lub ćwiczenie dla swojej klasy i poziomu.'
}, {
  title: 'Wydrukuj',
  text: 'Wydrukuj karty albo wyświetl je na tablicy interaktywnej.'
}, {
  title: 'Wejdź do klasy',
  text: 'Prowadź lekcję z gotowym planem.'
}];
export const audienceItems = ['Uczysz języka polskiego w szkole podstawowej', 'Masz klasy 4–6, 7–8 – albo wszystkie naraz', 'Przygotowujesz ósmoklasistów do egzaminu', 'Prowadzisz zastępstwa z polskiego', 'Dopiero zaczynasz pracę jako polonista', 'Nie chcesz co tydzień planować lekcji od zera', 'Chcesz, żeby uczniowie nie nudzili się na lekcji', 'Chcesz mieć pod ręką sprawdziany, klucze i kryteria'];
export const timeValueTasks = ['Planowanie każdej lekcji od zera', 'Szukanie kart pracy do lektur', 'Dopasowanie ćwiczeń do różnych poziomów', 'Przygotowanie sprawdzianów i kluczy', 'Pisanie komentarzy do ocen'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 cyfrowych tomów (240 stron)',
  detail: 'Plan i lekcje, lektury, gramatyka i ortografia, formy wypowiedzi, egzamin ósmoklasisty, ocenianie'
}, {
  label: '5 bonusów bez dodatkowych kosztów',
  detail: 'Rozgrzewki językowe, dyktanda, zastępstwa, komunikacja z rodzicami i plakaty do sali'
}, {
  label: 'Dla klas 4–8',
  detail: 'Lekcje minuta po minucie i karty pracy w 3 poziomach'
}];
export const offerIncludes = ['Natychmiastowy dostęp do pobrania', 'Pliki PDF do zapisania', 'Materiały do druku', 'Nie wysyłamy produktu fizycznego'];
export const PRICE = '99 zł';
export const OLD_PRICE = '199 zł';

// No testimonials on this page.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
  topic: string;
}> = [];
export const faqs = [{
  q: 'Jak otrzymam materiały?',
  a: 'POLSKI GOTOWY™ jest produktem cyfrowym. Po zakupie otrzymujesz dostęp do plików PDF, które możesz pobrać i zapisać. Możesz korzystać z nich na komputerze, tablecie lub telefonie i drukować to, czego potrzebujesz.'
}, {
  q: 'Czy materiały są zgodne z podstawą programową?',
  a: 'Materiały są oparte na podstawie programowej języka polskiego dla szkoły podstawowej: kształcenie literackie i kulturowe, kształcenie językowe, tworzenie wypowiedzi i samokształcenie. To niezależny materiał dla nauczycieli – nie jest publikacją MEN ani CKE.'
}, {
  q: 'Czy w środku są oficjalne arkusze CKE?',
  a: 'Nie. Tom 5 zawiera autorskie teksty i zadania w stylu egzaminu ósmoklasisty, z kluczem odpowiedzi. Dzięki temu możesz ćwiczyć z uczniami bez powtarzania arkuszy, które już znają.'
}, {
  q: 'Dla których klas są materiały?',
  a: 'Dla klas 4–8 szkoły podstawowej. Plan wynikowy jest rozpisany osobno dla klas 4–6 i 7–8, a karty pracy i ćwiczenia mają 3 poziomy trudności, więc sprawdzą się w każdej klasie.'
}, {
  q: 'Jakie lektury obejmuje Tom 2?',
  a: '16 lektur, m.in. Mikołajek, Akademia pana Kleksa, Opowieści z Narnii, mity greckie, legendy polskie, bajki Krasickiego, Chłopcy z Placu Broni, Opowieść wigilijna, Latarnik, Zemsta, Balladyna, Dziady cz. II, Treny, Pan Tadeusz (fragmenty), Mały Książę i Kamienie na szaniec.'
}, {
  q: 'Czy naprawdę przygotuję lekcję w 5 minut?',
  a: 'POLSKI GOTOWY™ powstał po to, by przyspieszyć przygotowanie lekcji. Otwierasz tom, wybierasz gotową lekcję lub kartę pracy i zapoznajesz się z nią w około pięć minut. Czas może się różnić w zależności od klasy i potrzebnych dostosowań.'
}, {
  q: 'Czy ćwiczenia mają klucz odpowiedzi?',
  a: 'Tak. Karty pracy do lektur, ćwiczenia z gramatyki i ortografii, zestawy w stylu egzaminu ósmoklasisty oraz sprawdziany A/B mają klucz odpowiedzi.'
}, {
  q: 'Czy mogę wydrukować materiały?',
  a: 'Tak. Wszystko jest w PDF do druku: karty pracy, ćwiczenia, sprawdziany i plakaty. Nie musisz drukować wszystkiego – część materiałów możesz wyświetlić na tablicy interaktywnej.'
}, {
  q: 'Czy pięć bonusów jest w cenie?',
  a: `Tak. Wszystkich pięć bonusów jest w pakiecie bez dodatkowych kosztów – całość za ${PRICE}.`
}, {
  q: 'Czy to subskrypcja?',
  a: 'Nie. Płacisz jednorazowo i otrzymujesz dostęp do materiałów cyfrowych. Bez żadnych opłat cyklicznych.'
}, {
  q: 'Czy jest gwarancja?',
  a: 'Tak. POLSKI GOTOWY™ objęty jest 7-dniową gwarancją satysfakcji. Jeśli materiały nie spełnią Twoich oczekiwań, możesz skontaktować się z nami i poprosić o zwrot pieniędzy w okresie gwarancji, zgodnie z jej warunkami.'
}];
export const footerLinks = ['Regulamin', 'Polityka prywatności', 'Polityka zwrotów', 'Kontakt'];
